/**
 * ER-12245 — welcome-wizard model (MDP-621).
 *
 * The backend (ER-12241) owns the flow, the page order, each page's status and
 * the landing path. This module turns that payload into the shape the
 * presentational wizard understands, and answers the two questions the entry
 * points ask: which page to open, and where to go next.
 *
 * It holds no Vue state so the router, Login.vue and the view share exactly one
 * definition of the rules (Confluence "Invite and Onboarding Wizards — Rules":
 * 3.5 one segment per step, 3.6 resume at the first unfinished step, 6.2 a
 * completed page is never shown again, 6.3 end when no page is left).
 */

import { getWelcomeWizardState } from "@/api/userAPI";

/** Route that hosts the wizard. Declared in `router/index.js`. */
export const WELCOME_WIZARD_ROUTE_NAME = "WelcomeWizard";
export const WELCOME_WIZARD_ROUTE_PATH = "/welcome-wizard";

/**
 * Backend page keys → the component types `OnboardingWizard` renders.
 * The backend calls the last Employee page `readiness_badges`; the component
 * knows it as `readiness`.
 */
export const STEP_COMPONENT_TYPES = {
  welcome: "welcome",
  profile: "profile",
  readiness_badges: "readiness",
  // ER-12246: the administrator-only pages.
  branding: "branding",
  iq_agent: "iq_agent",
  website: "website",
  manage_organization: "manage_organization",
};

/**
 * Page statuses that are behind the user and must never be shown again.
 */
export const CLOSED_STEP_STATUSES = ["completed", "dismissed"];

/**
 * Welcome copy per flow (rule 3.2: "Welcome per flow (copy in Figma)").
 *
 * ER-12245 owns the Welcome step for **all three** flows — ER-12246 only owns
 * the four administrator-only pages. Each flow gets its own wording; a flow
 * without an entry here keeps its normal landing rather than showing another
 * flow's welcome to the user.
 *
 * The administrator wording follows the rules page (3.2 "The Admin Welcome
 * tells the administrator what to expect in the Readiness module" and "The
 * Admin + Employee Welcome explains the two roles"). TODO(ER-12245): confirm
 * the final strings against the Figma frames linked on the ticket — only the
 * structure and the videos come from Eric's comment (2026-10-10).
 *
 * The Admin + Employee welcome reuses the Admin video until a combined one is
 * published (Eric, 2026-10-10); the embed link is permanent, so swapping it
 * later is a one-line change.
 */
const WELCOME_COPY = {
  employee: (accountName) => ({
    introText: `${accountName} uses exIQtive to clarify employees roles and responsibilities, train them and continuously evaluate performance through micro performance evaluations.`,
    subText:
      "Micro performance evaluations determine your readiness level for each responsibility assigned to you. For each responsibility you are expected to earn a readiness badge by:",
    pillars: [
      "Acquiring the necessary knowledge",
      "Demonstrating ability",
      "Proving consistent execution",
    ],
    buttonLabel: "Get started",
    videoEmbedUrl: "https://watchclueso.com/embed/zq8fn7m1w1fu7if4",
    videoTitle: "How to get started",
    videoCaption: "Welcome as Employee first page · 1:27",
  }),

  admin: (accountName) => ({
    introText: `${accountName} uses exIQtive to clarify employees roles and responsibilities, train them and continuously evaluate performance through micro performance evaluations.`,
    subText:
      "As an administrator you set up the organization so exIQtive can measure readiness. You will be guided through the pages below:",
    pillars: [
      "Adding your company logo",
      "Meeting IQ Agent, the organizational intelligence agent",
      "Adding your company website so IQ Agent learns about your organization",
      "Managing your employees, departments, positions, roles, responsibilities and assignments",
    ],
    buttonLabel: "Get started",
    videoEmbedUrl: "https://watchclueso.com/embed/ax06bcsieel1sxh8",
    videoTitle: "How to get started",
    videoCaption: "Welcome as Administrator first page · 1:26",
  }),

  admin_employee: (accountName) => ({
    introText: `${accountName} uses exIQtive to clarify employees roles and responsibilities, train them and continuously evaluate performance through micro performance evaluations.`,
    subText:
      "In this account you hold two roles. As an administrator you set up the organization and IQ Agent; as an employee you earn readiness badges for the responsibilities assigned to you.",
    pillars: [
      "Setting up your organization and IQ Agent",
      "Earning readiness badges for your responsibilities",
      "Keeping one profile for both of your roles",
    ],
    buttonLabel: "Get started",
    // Eric (2026-10-10): the Admin Welcome video is used for Admin + Employee
    // until a combined video exists. The embed link is permanent.
    videoEmbedUrl: "https://watchclueso.com/embed/ax06bcsieel1sxh8",
    videoTitle: "How to get started",
    videoCaption: "Welcome as Administrator first page · 1:26",
  }),
};

/**
 * Clueso videos for the administrator pages (Eric Kish, ER-12245 comment of
 * 2026-10-10). The logo and website pages have no video yet, so the media panel
 * shows its placeholder for them.
 */
const ADMIN_PAGE_MEDIA = {
  branding: { videoEmbedUrl: "", videoCaption: "Add your company logo" },
  website: { videoEmbedUrl: "", videoCaption: "Add your company website" },
  iq_agent: {
    videoEmbedUrl: "https://watchclueso.com/embed/d5z9xgv0id09nopo",
    videoTitle: "How to get started",
    videoCaption: "IQ - Organizational Intelligence Agent · 0:53",
  },
  manage_organization: {
    videoEmbedUrl: "https://watchclueso.com/embed/yp2c3021zf17q8cc",
    videoTitle: "How to get started",
    videoCaption: "Manage your organization · 1:37",
  },
};

/**
 * Set your profile subtitle per flow (ER-12762, Eric Kish 2026-10-30): the
 * administrator flows talk about the name across exIQtive, the Employee-only flow
 * keeps the readiness-badges wording.
 */
const PROFILE_SUBTITLE_ADMIN =
  "Help us personalize your experience. Your photo appears next to your name across exIQtive so your team can recognize you.";
const PROFILE_SUBTITLE_EMPLOYEE =
  "Help us personalize your experience. Your photo appears on your readiness badges so coaches and inspectors can recognize you.";

const profileSubtitle = (flow) =>
  flow === "admin" || flow === "admin_employee"
    ? PROFILE_SUBTITLE_ADMIN
    : PROFILE_SUBTITLE_EMPLOYEE;

/**
 * The readiness page is shown by the Employee and Admin + Employee flows only;
 * the administrator-only flow ends at Manage your organization.
 */
const readinessCopy = (flow) => ({
  title: "Earn your readiness badges",
  introText:
    "Every responsibility assigned to you has its own badge. The badge level shows how ready you are for that responsibility.",
  levels: [
    { text: "You have acquired the knowledge the responsibility requires.", color: "#e58700" },
    { text: "You have demonstrated the ability to perform it on your own.", color: "#00b813" },
    {
      text: "You have proven consistent execution over time and can guide others.",
      color: "#00a3ff",
    },
    {
      text: "You are the reference — you improve how the responsibility is done.",
      color: "#2e1ec1",
    },
  ],
  noviceText: "Until you earn a badge for a responsibility, you are a Novice for it.",
  // Rule 3 / ticket scope: Employment flow lands on Your Stuff, Admin + Employee
  // on Organization, so the final button follows the flow.
  buttonLabel:
    flow === "admin_employee" ? "Go to your organization" : "Go to your responsibilities",
});

/** The component type for a backend page key, or `null` when it has none yet. */
export function componentTypeForStep(pageKey) {
  return STEP_COMPONENT_TYPES[pageKey] || null;
}

/** Whether this build has the Welcome wording for `flow`. */
export function hasWelcomeCopy(flow) {
  return Boolean(WELCOME_COPY[flow]);
}

/**
 * Whether this build can draw the page for `flow`.
 *
 * A page is drawable when its component is registered and, for Welcome, when
 * that flow's wording exists.
 */
export function isStepRenderable(pageKey, flow) {
  const type = componentTypeForStep(pageKey);
  if (type === null) return false;
  if (type === "welcome") return hasWelcomeCopy(flow);
  return true;
}

/**
 * Whether the wizard can open for this flow right now.
 *
 * Only the pages the user will actually be shown matter, and only the ones this
 * build can draw. ER-12246 owns the four administrator pages: until it
 * registers their components, the wizard opens on the pages that do exist and
 * `nextRenderableOpenIndex` ends the session before the first page it cannot
 * draw. Requiring *every* open page to be drawable kept the whole administrator
 * flow dark, which blocked QA (2026-10-26) and ER-12246 itself.
 */
export function isFlowRenderable(flow, steps) {
  const open = (steps || []).filter(isOpenStep);
  if (!open.length) return false;
  return open.some((step) => isStepRenderable(step.key, flow));
}

/** Whether a step is still ahead of the user. A skipped page is shown again (6.2). */
export function isOpenStep(step) {
  return !CLOSED_STEP_STATUSES.includes(step?.status);
}

/** Index of `pageKey` in `steps`, or -1. */
export function indexOfStep(steps, pageKey) {
  return (steps || []).findIndex((step) => step.key === pageKey);
}

/** First step after `from` that is still to do, or -1 when the wizard is over. */
export function nextOpenIndex(steps, from) {
  for (let index = from + 1; index < (steps || []).length; index += 1) {
    if (isOpenStep(steps[index])) return index;
  }
  return -1;
}

/**
 * First step after `from` that is both still to do **and** drawable.
 *
 * The wizard must never advance onto a page this build cannot render: a blank
 * step leaves the user with no way forward. If the next open pages all belong
 * to a component that does not exist yet, the wizard ends instead.
 */
export function nextRenderableOpenIndex(steps, from, flow) {
  let index = from;
  for (;;) {
    index = nextOpenIndex(steps, index);
    if (index === -1) return -1;
    if (isStepRenderable(steps[index].key, flow)) return index;
  }
}

/**
 * Previous step before `from` that is still to do, or -1.
 *
 * Back never lands on a completed page: Welcome, Meet IQ Agent, Manage your
 * organization and Earn your readiness badges are shown once (rule 6.2).
 *
 * When `flow` is given, Back also skips pages this build cannot draw, so it can
 * never land on a blank step — possible while ER-12246's administrator pages
 * are still missing and the wizard advanced past them.
 */
export function previousOpenIndex(steps, from, flow = null) {
  for (let index = from - 1; index >= 0; index -= 1) {
    const step = steps[index];
    if (!isOpenStep(step)) continue;
    if (flow && !isStepRenderable(step.key, flow)) continue;
    return index;
  }
  return -1;
}

/**
 * Where the wizard opens. Rule 3.6: resume at the first unfinished step. The
 * backend already names it, so prefer `current_step` and fall back to the first
 * open step in case it is missing.
 */
export function initialStepIndex(steps, currentStep) {
  const named = indexOfStep(steps, currentStep);
  if (named !== -1) return named;
  for (let index = 0; index < (steps || []).length; index += 1) {
    if (isOpenStep(steps[index])) return index;
  }
  return 0;
}

/**
 * Turn an absolute backend landing URL into a router location.
 *
 * `landing_path` is built with `build_frontend_url`, so it arrives as
 * `https://account.dev.exiqtive.com/organization?accountId=24068`. Pushing that
 * whole string would leave the SPA; the path and query are what the router needs.
 */
export function toRouterLocation(landingPath) {
  if (!landingPath) return "/";
  try {
    const url = new URL(landingPath, "http://localhost");
    return `${url.pathname}${url.search}`;
  } catch (error) {
    return landingPath;
  }
}

/**
 * The props for one step. Unrenderable steps still keep their segment (rule
 * 3.5) but carry no content, so the container renders nothing for them.
 */
export function pageForStep(step, context = {}) {
  const {
    flow,
    accountName = "",
    logoUrl = "",
    profile = null,
    firstName = "",
    websites = [],
  } = context;
  const pageKey = step?.key;
  const type = componentTypeForStep(pageKey);

  if (type === "welcome") {
    const copy = WELCOME_COPY[flow];
    if (!copy) return { type: pageKey };
    return {
      type,
      firstName: profile?.first_name || firstName,
      accountName,
      logo: logoUrl,
      ...copy(accountName || "Your organization"),
    };
  }

  if (type === "profile") {
    return {
      type,
      firstName: profile?.first_name || firstName,
      lastName: profile?.last_name || "",
      photoUrl: profile?.picture || "",
      subtitle: profileSubtitle(flow),
    };
  }

  if (type === "readiness") {
    return { type, ...readinessCopy(flow), showMedia: false };
  }

  if (type === "branding") {
    return { type, accountName, ...ADMIN_PAGE_MEDIA.branding };
  }

  if (type === "website") {
    // The account's saved websites are prefilled: saving replaces the whole list.
    return { type, websites, ...ADMIN_PAGE_MEDIA.website };
  }

  if (type === "iq_agent") {
    return { type, ...ADMIN_PAGE_MEDIA.iq_agent };
  }

  if (type === "manage_organization") {
    return {
      type,
      // Admin + Employee carries on to the readiness page, so it only continues.
      buttonLabel: flow === "admin_employee" ? "Continue" : "Go to your organization",
      ...ADMIN_PAGE_MEDIA.manage_organization,
    };
  }

  // A page key this build does not know: keep the segment, draw nothing.
  return { type: pageKey };
}

/** Build the `pages` prop: one entry per backend step, in wizard order (3.5). */
export function buildWizardPages(steps, context = {}) {
  return (steps || []).map((step) => pageForStep(step, context));
}

/**
 * Per-account "do not open the wizard again this session" marker.
 *
 * Only set when the view could not load its state: the entry gate already fails
 * open on error, but the gate and the view call the same endpoint, so a failure
 * after entry would otherwise bounce the user between `/` and the wizard.
 * Scoped by account so switching accounts is not affected.
 */
const WIZARD_BYPASS_PREFIX = "welcomeWizardBypass:";

function bypassKey(accountId) {
  return `${WIZARD_BYPASS_PREFIX}${accountId}`;
}

export function setWizardBypass(accountId) {
  if (!accountId) return;
  try {
    sessionStorage.setItem(bypassKey(accountId), "1");
  } catch (error) {
    /* storage unavailable — the entry gate still fails open on error */
  }
}

export function isWizardBypassed(accountId) {
  if (!accountId) return false;
  try {
    return sessionStorage.getItem(bypassKey(accountId)) === "1";
  } catch (error) {
    return false;
  }
}

export function clearWizardBypass(accountId) {
  if (!accountId) return;
  try {
    sessionStorage.removeItem(bypassKey(accountId));
  } catch (error) {
    /* nothing to clear */
  }
}

/**
 * Drop every account's bypass marker.
 *
 * The marker means "do not reopen the wizard for the rest of this browser
 * session". Signing out and back in is a new session, but `sessionStorage`
 * survives it, so without this a page the user skipped never came back — QA saw
 * it only return in a new tab (2026-10-26). Called on sign-in and sign-out.
 */
export function clearAllWizardBypasses() {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(WIZARD_BYPASS_PREFIX))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch (error) {
    /* storage unavailable — nothing to clear */
  }
}

/** Read the ids the wizard endpoints key on. */
export function readWelcomeWizardIds() {
  try {
    const accessPermission = JSON.parse(localStorage.getItem("accessPermission") || "{}");
    return {
      accountId: localStorage.getItem("currentAccountID"),
      accessId: accessPermission?.id ?? null,
    };
  } catch (error) {
    return { accountId: null, accessId: null };
  }
}

/**
 * Decide whether the wizard replaces the landing page (rule 3.6).
 *
 * Returns the wizard path, or `null` to keep the caller's normal landing. This
 * runs on the sign-in path, so it fails open: a flag-off backend answers 404 and
 * any network error must leave the user exactly where they are today.
 *
 * @param {{
 *   accountId?: number|string|null,
 *   accessId?: number|string|null,
 *   enabled: boolean,
 *   fetchState?: (accountId: string, accessId: string) => Promise<unknown>,
 * }} options
 * @returns {Promise<string|null>}
 */
export async function resolveWelcomeWizardEntry(options) {
  const { accountId, accessId, enabled, fetchState = getWelcomeWizardState } = options || {};

  if (!enabled || !accountId || !accessId) return null;
  if (isWizardBypassed(accountId)) return null;

  try {
    const response = await fetchState(accountId, accessId);
    const state = response?.data ?? response;

    // Rule 6.3: no page left, the wizard is over.
    if (!state?.current_step) return null;
    // Capability-based: open only when every page the user will be shown can be
    // drawn. ER-12246's components turn the administrator flows on by existing.
    if (!isFlowRenderable(state.flow, state.steps)) return null;
    if (!isStepRenderable(state.current_step, state.flow)) return null;

    return WELCOME_WIZARD_ROUTE_PATH;
  } catch (error) {
    return null;
  }
}
